import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import '../../styles/pages.css';
import '../../styles/dashboard.css';

const COLORS = ['#dc2626', '#991b1b', '#7f1d1d'];

export default function DashboardPage() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const entityId = user.entity;

  const [stats, setStats] = useState({
    totalSent: 0,
    totalFailed: 0,
    totalContacts: 0,
    balance: 0
  });
  const [monthlyData, setMonthlyData] = useState([]);
  const [deliveryStats, setDeliveryStats] = useState({ sent: 0, failed: 0 });
  const [recentSMS, setRecentSMS] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (entityId) {
      fetchDashboardData();
    }
  }, [entityId]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Fetching dashboard data for entity:', entityId);

      const [statsRes, monthlyRes, deliveryRes, recentRes] = await Promise.all([
        api.get(`/api/dashboard/stats/${entityId}`),
        api.get(`/api/dashboard/monthly-stats/${entityId}`),
        api.get(`/api/dashboard/delivery-stats/${entityId}`),
        api.get(`/api/dashboard/recent-sms/${entityId}`)
      ]);

      console.log('Stats:', statsRes.data);
      console.log('Monthly data:', monthlyRes.data);
      console.log('Delivery stats:', deliveryRes.data);

      setStats(statsRes.data || {});
      setMonthlyData(monthlyRes.data || []);
      setDeliveryStats(deliveryRes.data || {});
      setRecentSMS(recentRes.data || []);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load dashboard data. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const deliveryRate = stats.totalSent > 0
    ? ((stats.totalSent / (stats.totalSent + stats.totalFailed)) * 100).toFixed(1)
    : 0;

  const pieData = [
    { name: 'Delivered', value: stats.totalSent },
    { name: 'Failed', value: stats.totalFailed }
  ];

  if (!entityId) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Dashboard</h1>
          <p>Please log in to view your dashboard</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Dashboard</h1>
          <p>Loading your dashboard...</p>
        </div>
        <div className="page-section">
          <p>Loading data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Welcome to your SMS management dashboard</p>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fee2e2' }}>📨</div>
          <div className="stat-content">
            <h3>Total SMS Sent</h3>
            <p className="stat-number">{stats.totalSent.toLocaleString()}</p>
            <span className="stat-trend">All time messages</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#dcfce7' }}>✓</div>
          <div className="stat-content">
            <h3>Delivery Rate</h3>
            <p className="stat-number">{deliveryRate}%</p>
            <span className="stat-trend">
              {deliveryRate >= 95 ? 'Excellent' : deliveryRate >= 80 ? 'Good' : 'Needs improvement'}
            </span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fef3c7' }}>💰</div>
          <div className="stat-content">
            <h3>Account Balance</h3>
            <p className="stat-number">${stats.balance?.toFixed(2) || '0.00'}</p>
            <span className="stat-trend">Available funds</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fce7f3' }}>📞</div>
          <div className="stat-content">
            <h3>Contacts</h3>
            <p className="stat-number">{stats.totalContacts?.toLocaleString() || '0'}</p>
            <span className="stat-trend">Registered contacts</span>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      {monthlyData.length > 0 || (stats.totalSent > 0 || stats.totalFailed > 0) ? (
        <div className="charts-grid">
          {/* Monthly Bar Chart */}
          <div className="page-section chart-section">
            <h3>Monthly Messages Sent</h3>
            {monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyData} margin={{ bottom: 80, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="monthName"
                    stroke="#64748b"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                  />
                  <YAxis stroke="#64748b" />
                  <Tooltip
                    contentStyle={{ background: '#fff', border: '1px solid #dc2626' }}
                    cursor={{ fill: 'rgba(220, 38, 38, 0.1)' }}
                  />
                  <Bar dataKey="count" fill="#dc2626" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-muted" style={{ padding: '2rem', textAlign: 'center' }}>
                No data available yet
              </p>
            )}
          </div>

          {/* Delivery Pie Chart */}
          <div className="page-section chart-section">
            <h3>Delivery Statistics</h3>
            {stats.totalSent > 0 || stats.totalFailed > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    <Cell fill="#16a34a" />
                    <Cell fill="#dc2626" />
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: '#fff', border: '1px solid #dc2626' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-muted" style={{ padding: '2rem', textAlign: 'center' }}>
                No data available yet
              </p>
            )}
          </div>
        </div>
      ) : null}

      {/* Recent SMS Table */}
      <div className="page-section">
        <h3>Recent SMS Messages (Last 20)</h3>
        <div className="table-wrapper">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Phone Number</th>
                <th>Message</th>
                <th>Sent Time</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentSMS.length > 0 ? (
                recentSMS.map((sms) => (
                  <tr key={sms.id}>
                    <td className="phone-cell">{sms.PhoneNumber}</td>
                    <td className="message-cell">
                      <span className="message-preview">
                        {sms.Message.substring(0, 50)}
                        {sms.Message.length > 50 ? '...' : ''}
                      </span>
                    </td>
                    <td className="time-cell">
                      {new Date(sms.TimePosted).toLocaleString()}
                    </td>
                    <td>
                      <span className="status-badge delivered">✓ Delivered</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="empty-state">
                    No SMS messages sent yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
